<template>
    <kgr-form-scaffold
        :with-primary-buttons="false"
        edit-mode
        @backBtnClicked="redirectToOverview"
    >
        <kgr-members-relations-data-provider
            v-slot="relationsScope"
            :member-id="memberId"
        >
            <div class="animated fadeIn">
                <h1 class="kgr-headline kgr-headline--h2 kgr-headline--text kgr-headline--firstInText">
                    {{ $t('Familienmitglieder') }}
                </h1>
                <kgr-member-relations-form
                    :is-creatable="isCreatable"
                    :is-deletable="isDeletable"
                    :club-id="clubId"
                    :member-id="memberId"
                    :relations="relationsScope.data[0]"
                    :legal-representatives="relationsScope.data[1]"
                    :pending-create="pendingRelationCreate"
                    :pending-remove="pendingRelationRemove"
                    class="animated fadeIn"
                    @addLegalRepresentative="addLegalRepresentative($event, relationsScope.data[1], relationsScope.reload)"
                    @addParentOf="onAddParentOf($event, relationsScope.reload)"
                    @addChildOf="onAddChildOf($event, relationsScope.reload)"
                    @addSiblingOf="onAddSiblingOf($event, relationsScope.reload)"
                    @addSpouseOf="onAddSpouseOf($event, relationsScope.reload)"
                    @addFamilyOf="onAddFamilyOf($event, relationsScope.reload)"
                    @removeRelation="onRemove($event, relationsScope.reload)"
                />

                <hr class="kgr-separator" />

                <h1
                    v-show="member !== null"
                    class="kgr-headline kgr-headline--h2 kgr-headline--text kgr-headline--firstInText"
                >
                    {{ tMemberTitle }}
                </h1>
                <kgr-member-legal-representatives-form
                    :is-creatable="isCreatable"
                    :is-deletable="isDeletable"
                    :member-id="memberId"
                    :member="member"
                    :club-id="clubId"
                    :relations="relationsScope.data[0]"
                    :legal-representatives="relationsScope.data[1]"
                    :pending-update="pendingLegalRepresentativesUpdate"
                    @add="addLegalRepresentative($event, relationsScope.data[1], relationsScope.reload)"
                    @remove="removeLegalRepresentative($event, relationsScope.data[1], relationsScope.reload)"
                />
            </div>
        </kgr-members-relations-data-provider>
    </kgr-form-scaffold>
</template>

<script>
import FormScaffold from '../../components/common/form/FormScaffold';
import MemberLegalRepresentativesForm from '../../components/members/MemberLegalRepresentativesForm';
import MemberRelationsForm from '../../components/members/MemberRelationsForm';
import MemberRepository from '../../js/repository/MemberRepository';
import MembersRelationsDataProvider from '../../components/data-provider/MembersRelationsDataProvider';

export default {
    components: {
        'kgr-members-relations-data-provider': MembersRelationsDataProvider,
        'kgr-member-relations-form': MemberRelationsForm,
        'kgr-member-legal-representatives-form': MemberLegalRepresentativesForm,
        'kgr-form-scaffold': FormScaffold,
    },
    props: {
        overviewUrl: {
            type: String,
            required: true,
        },
        memberId: {
            type: Number,
            required: true,
        },
        clubId: {
            type: Number,
            required: true,
        },
        isCreatable: {
            type: Boolean,
            required: true,
        },
        isDeletable: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            pendingRelationCreate: false,
            pendingRelationRemove: false,
            pendingLegalRepresentativesUpdate: false,
            member: null,
        };
    },
    computed: {
        tMemberTitle() {
            if (this.member === null) {
                return '';
            }
            return this.$t('member.legalRepresentatives.title', { fullName: this.member.fullName() });
        },
    },
    created() {
        MemberRepository.find(this.memberId)
            .then((member) => this.member = member)
            .catch(() => this.$notify.error('error.loadingData'));
    },
    methods: {
        redirectToOverview() {
            window.location = this.overviewUrl;
        },
        onAddParentOf(member, reload) {
            this.onAdd(member.id, 'parent', reload);
        },
        onAddChildOf(member, reload) {
            this.onAdd(member.id, 'child', reload);
        },
        onAddSiblingOf(member, reload) {
            this.onAdd(member.id, 'sibling', reload);
        },
        onAddSpouseOf(member, reload) {
            this.onAdd(member.id, 'spouse', reload);
        },
        onAddFamilyOf(member, reload) {
            this.onAdd(member.id, 'family', reload);
        },
        onAdd(relatedMemberId, type, reload) {
            this.pendingRelationCreate = true;

            MemberRepository.addRelation(this.memberId, relatedMemberId, type)
                .then(() => {
                    this.pendingRelationCreate = false;
                    this.$notify.success('Neue Beziehung erfolgreich hinzugefügt');
                    this.relationHuman = null;
                    reload();
                })
                .catch(() => {
                    this.pendingRelationCreate = false;
                    this.$notify.error('saveChanges.error');
                });
        },
        onRemove(relation, reload) {
            this.pendingRelationRemove = true;
            MemberRepository.deleteRelation(this.memberId, relation.id)
                .then(() => {
                    this.pendingRelationRemove = false;
                    this.$notify.success('Beziehung erfolgreich entfernt');
                    reload();
                })
                .catch(() => {
                    this.pendingRelationRemove = false;
                    this.$notify.error('saveChanges.error');
                });
        },
        addLegalRepresentative(member, legalRepresentatives, reload) {
            const legalRepresentativesIds = legalRepresentatives.map((legalRepresentative) => legalRepresentative.id);
            legalRepresentativesIds.push(member.id);

            this.updateLegalRepresentative(legalRepresentativesIds, reload);
        },
        removeLegalRepresentative(member, legalRepresentatives, reload) {
            this.updateLegalRepresentative(
                legalRepresentatives
                    .filter((legalRepresentative) => legalRepresentative.id !== member.id)
                    .map((legalRepresentative) => legalRepresentative.id),
                reload,
            );
        },
        updateLegalRepresentative(legalRepresentativesIds, reload) {
            this.pendingLegalRepresentativesUpdate = true;

            MemberRepository.updateLegalRepresentatives(this.memberId, legalRepresentativesIds)
                .then(() => {
                    this.pendingLegalRepresentativesUpdate = false;
                    this.$notify.success('Vertretungsberechtigung erfolgreich aktualisiert');
                    reload();
                })
                .catch(() => {
                    this.pendingLegalRepresentativesUpdate = false;
                    this.$notify.error('saveChanges.error');
                });
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss"></style>
